/**
 * Aircraft Calendar Page - Phase X2
 * 
 * Main calendar page with toggle between:
 * - Per-drone calendar view (react-big-calendar)
 * - Fleet-wide timeline view (custom CSS Grid)
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../lib/api';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Calendar as CalendarIcon, 
  Users, 
  Plane,
  AlertTriangle,
  ArrowUpRight
} from 'lucide-react';
import PerDroneCalendar from '../components/calendar/PerDroneCalendar';
import DroneFleetTimeline from '../components/calendar/DroneFleetTimeline';

const AircraftCalendarPage = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  
  // State
  const [viewMode, setViewMode] = useState('fleet'); // 'drone' | 'fleet'
  const [selectedDroneId, setSelectedDroneId] = useState(null);
  const [drones, setDrones] = useState([]);
  const [scheduleData, setScheduleData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({
    start: dayjs().startOf('month').format('YYYY-MM-DD'),
    end: dayjs().endOf('month').format('YYYY-MM-DD')
  });
  
  // Translations - get calendar translations with fallback
  const cal = t.aircraft?.calendar || {};
  
  // Set document title
  useEffect(() => {
    document.title = `${cal.title || 'Flight Schedule'} | Fleetora`;
  }, [cal.title]);
  
  // Fetch aircraft list
  useEffect(() => {
    const fetchDrones = async () => {
      try {
        const response = await api.get('/aircraft');
        setDrones(response.data || []);
        if (response.data?.length > 0 && !selectedDroneId) {
          setSelectedDroneId(response.data[0].id);
        }
      } catch (err) {
        console.error('Failed to fetch aircraft:', err);
      }
    };
    fetchDrones();
  }, []);
  
  // Fetch schedule data
  const fetchSchedule = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      if (viewMode === 'drone' && selectedDroneId) {
        const response = await api.get(`/aircraft/${selectedDroneId}/schedule`, {
          params: {
            start: dateRange.start,
            end: dateRange.end,
            lang: language
          }
        });
        setScheduleData({
          type: 'drone',
          drone: drones.find(d => d.id === selectedDroneId),
          events: response.data.events || [],
          telemetry_status: response.data.telemetry_status
        });
      } else {
        const response = await api.get('/aircraft/fleet/schedule', {
          params: {
            start: dateRange.start,
            end: dateRange.end,
            lang: language
          }
        });
        setScheduleData({
          type: 'fleet',
          drones: response.data.drones || [],
          total_events: response.data.total_events
        });
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.upgrade_required) {
        setError({
          type: 'tier_limit',
          message: language === 'cs' ? detail.message_cs : detail.message_en
        });
      } else {
        setError({
          type: 'error',
          message: language === 'cs' 
            ? 'Nepodařilo se načíst rozvrh'
            : 'Failed to load schedule'
        });
      }
    } finally {
      setLoading(false);
    }
  }, [viewMode, selectedDroneId, dateRange, language, drones]);
  
  // Fetch on mount and when dependencies change
  useEffect(() => {
    if (drones.length > 0) {
      fetchSchedule();
    }
  }, [fetchSchedule, drones.length]);
  
  // Handle date navigation from calendar components
  const handleNavigate = useCallback((newDate, view) => {
    let start, end;
    
    switch (view) {
      case 'day':
        start = dayjs(newDate).startOf('day');
        end = dayjs(newDate).endOf('day');
        break;
      case 'week':
        start = dayjs(newDate).startOf('week');
        end = dayjs(newDate).endOf('week');
        break;
      case 'month':
      default:
        start = dayjs(newDate).startOf('month');
        end = dayjs(newDate).endOf('month');
        break;
    }
    
    setDateRange({
      start: start.format('YYYY-MM-DD'),
      end: end.format('YYYY-MM-DD')
    });
  }, []);
  
  // Handle event click - navigate to mission detail
  const handleEventClick = useCallback((event) => {
    if (event?.mission_id) {
      navigate(`/missions/${event.mission_id}`);
    }
  }, [navigate]);
  
  // Handle empty slot click - open mission creation
  const handleSlotClick = useCallback((slotData) => {
    // Navigate to mission creation with pre-filled data
    const params = new URLSearchParams({
      aircraft_id: slotData.aircraft_id,
      etot: slotData.etot
    });
    navigate(`/missions/create?${params.toString()}`);
  }, [navigate]);
  
  return (
    <div className="p-6 space-y-6" data-testid="aircraft-calendar-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="page-title">
            <CalendarIcon className="h-6 w-6 text-blue-600" />
            {cal.title}
          </h1>
          <p className="text-sm text-[var(--text-muted)] mt-1" data-testid="page-subtitle">
            {cal.subtitle}
          </p>
        </div>
        
        {/* View toggle */}
        <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
          <Button
            variant={viewMode === 'drone' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('drone')}
            className="gap-2"
            data-testid="view-toggle-drone"
          >
            <Plane className="h-4 w-4" />
            {cal.perDrone}
          </Button>
          <Button
            variant={viewMode === 'fleet' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('fleet')}
            className="gap-2"
            data-testid="view-toggle-fleet"
          >
            <Users className="h-4 w-4" />
            {cal.fleet}
          </Button>
        </div>
      </div>
      
      {/* Drone selector for per-drone view */}
      {viewMode === 'drone' && (
        <Card className="border-[var(--border-default)]">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <label className="font-medium text-sm">
                {cal.selectDrone}
              </label>
              <Select
                value={selectedDroneId || ''}
                onValueChange={setSelectedDroneId}
              >
                <SelectTrigger className="w-[300px]" data-testid="drone-selector">
                  <SelectValue placeholder={cal.selectDrone} />
                </SelectTrigger>
                <SelectContent>
                  {drones.map(drone => (
                    <SelectItem key={drone.id} value={drone.id}>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{drone.model}</span>
                        <span className="text-gray-500">
                          ({drone.registration_number || drone.serial_number})
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Error display */}
      {error && (
        <Card className="border-amber-300 bg-amber-50 dark:bg-amber-900/20">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-amber-800 dark:text-amber-200">
                {error.message}
              </p>
              {error.type === 'tier_limit' && (
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 h-auto text-amber-700 dark:text-amber-300 mt-1"
                  onClick={() => navigate('/settings/subscription')}
                >
                  {cal.upgradePlan}
                  <ArrowUpRight className="h-3 w-3 ml-1" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Calendar views */}
      {!error && (
        <>
          {viewMode === 'drone' && scheduleData?.type === 'drone' && (
            <PerDroneCalendar
              drone={scheduleData.drone}
              events={scheduleData.events}
              loading={loading}
              language={language}
              translations={cal}
              onEventClick={handleEventClick}
              onSlotClick={handleSlotClick}
              onNavigate={handleNavigate}
            />
          )}
          
          {viewMode === 'fleet' && scheduleData?.type === 'fleet' && (
            <DroneFleetTimeline
              drones={scheduleData.drones}
              loading={loading}
              language={language}
              translations={cal}
              onEventClick={handleEventClick}
              onSlotClick={handleSlotClick}
              onNavigate={handleNavigate}
            />
          )}
          
          {loading && !scheduleData && (
            <Card className="border-[var(--border-default)]">
              <CardContent className="h-[400px] flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </CardContent>
            </Card>
          )}
        </>
      )}
      
      {/* Stats footer */}
      {scheduleData && !error && (
        <div className="flex items-center gap-4 text-sm text-[var(--text-muted)]" data-testid="schedule-stats">
          <span>
            {cal.dateRange}{' '}
            {dayjs(dateRange.start).format('D. MMM')} - {dayjs(dateRange.end).format('D. MMM YYYY')}
          </span>
          {scheduleData.type === 'fleet' && (
            <>
              <span>•</span>
              <span>
                {scheduleData.drones?.length || 0} {cal.drones}
              </span>
              <span>•</span>
              <span>
                {scheduleData.total_events || 0} {cal.missions}
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AircraftCalendarPage;
